import {Block, Col, Row} from 'jsxstyle'
import React, {useState} from 'react'
import {motion} from 'framer-motion'

import Loader from '../../components/Loader'
import CustomToogle from '../../components/CustomToggle'
import Toast from '../../components/Toast'
import FormInput from '../../components/FormInput'
import DynamicFormField from '../../components/FormField/DynamicFormField'
import {UpdateSettingsBody} from '../../services/Settings/type'
import {ReactComponent as SightcallLogo} from '../../common/assets/sightcall-logo.svg'
import {SettingsContextProvider, useSettingsContext} from './SettingsContext'
import i18n from '../../common/i18n'

const Settings: React.FC = () => {
  const {settings, updateSettings, loading} = useSettingsContext()
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  const autoSave = async (settingKey: keyof UpdateSettingsBody, value: any) => {
    setIsSaving(true)

    try {
      await updateSettings({[settingKey]: value})
      setToastMessage('Settings saved successfully')
      setShowToast(true)
    } catch (error) {
      setToastMessage('Failed to save settings')
      setShowToast(true)
    } finally {
      setIsSaving(false)
    }
  }

  const handleToggleChange = (isChecked: boolean) => {
    autoSave('ragPipelineEnabled', isChecked)
  }

  const handleCompanyContextChange = (value: string) => {
    if (value === '<p></p>') {
      autoSave('companyContext', '')
    } else {
      autoSave('companyContext', value)
    }
  }

  const handleS3AccessKeyChange = (value: string) => {
    if (value[0] == '•') {
      return
    } else if (value === '<p></p>') {
      autoSave('s3AccessKey', '')
    } else {
      autoSave('s3AccessKey', value.trim())
    }
  }

  const handleS3SecretKeyChange = (value: string) => {
    if (value[0] == '•') {
      return
    } else if (value === '<p></p>') {
      autoSave('s3SecretKey', '')
    } else {
      autoSave('s3SecretKey', value.trim())
    }
  }

  const handleS3BucketNameChange = (value: string) => {
    if (value === '<p></p>') {
      autoSave('s3BucketName', '')
    } else {
      autoSave('s3BucketName', value.trim())
    }
  }

  const handleS3RegionChange = (value: string) => {
    if (value === '<p></p>') {
      autoSave('s3Region', '')
    } else {
      autoSave('s3Region', value.trim())
    }
  }

  const handleS3EndpointChange = (value: string) => {
    if (value === '<p></p>') {
      autoSave('s3Endpoint', '')
    } else {
      autoSave('s3Endpoint', value.trim())
    }
  }

  const handleWebhookEndpointChange = (value: string) => {
    if (value === '<p></p>') {
      autoSave('webhookEndpoint', '')
    } else {
      autoSave('webhookEndpoint', value.trim())
    }
  }

  if (loading) {
    return (
      <Row width="100%" height="100%" backgroundColor="#F5F5F5" justifyContent="center" alignItems="center">
        <Loader />
      </Row>
    )
  }

  return (
    <Row width="100%" height="100%" backgroundColor="#F5F5F5" padding="24px" overflow="scroll">
      <Toast
        message={toastMessage}
        showToast={showToast}
        setShowToast={setShowToast}
        color={toastMessage.includes('Failed') ? '#DC2626' : '#0066DB'}
        backgroundColor={toastMessage.includes('Failed') ? '#FEF2F2' : '#EAF3FF'}
      />

      <Col width="100%" maxWidth="800px" margin="0 auto">
        <Row alignItems="center" marginBottom="32px">
          <SightcallLogo width="40px" height="40px" />
          <Block marginLeft="16px" fontSize="28px" fontWeight={600} color="#1F2937">
            {i18n.t('xk.settings.title')}
          </Block>
        </Row>

        <motion.div initial={{opacity: 0, y: 20}} animate={{opacity: 1, y: 0}} transition={{duration: 0.3}}>
          <Block backgroundColor="#FFFFFF" borderRadius="12px" boxShadow="0 1px 3px rgba(0, 0, 0, 0.1)" padding="24px" marginBottom="24px">
            <Block fontSize="20px" fontWeight={600} color="#1F2937" marginBottom="20px">
              {i18n.t('xk.settings.configuration')}
            </Block>

            <Row alignItems="center" justifyContent="space-between" paddingBottom="16px">
              <Col flex="1">
                <Block fontSize="16px" fontWeight={500} color="#374151" marginBottom="4px">
                  {i18n.t('xk.settings.pipeline')}
                </Block>
                <Block fontSize="14px" color="#6B7280" lineHeight="1.5">
                  Enable or disable the Retrieval-Augmented Generation pipeline for enhanced content processing
                </Block>
              </Col>

              <Row alignItems="center" marginLeft="24px">
                {isSaving && (
                  <Block marginRight="12px" fontSize="12px" color="#6B7280">
                    {i18n.t('xk.settings.saving')}
                  </Block>
                )}
                <CustomToogle isChecked={settings?.ragPipelineEnabled || false} onChange={handleToggleChange} />
              </Row>
            </Row>

            <Row alignItems="center" marginTop="16px" paddingTop="16px" borderTop="1px solid #E5E7EB">
              <Block
                width="8px"
                height="8px"
                borderRadius="50%"
                backgroundColor={settings?.ragPipelineEnabled ? '#10B981' : '#6B7280'}
                marginRight="8px"
              />
              <Block fontSize="14px" color="#6B7280">
                {i18n.t('xk.settings.pipelineStatus')} {settings?.ragPipelineEnabled ? 'Active' : 'Inactive'}
              </Block>
            </Row>
          </Block>
        </motion.div>

        <motion.div initial={{opacity: 0, y: 20}} animate={{opacity: 1, y: 0}} transition={{duration: 0.3, delay: 0.1}}>
          <Block backgroundColor="#FFFFFF" borderRadius="12px" boxShadow="0 1px 3px rgba(0, 0, 0, 0.1)" padding="24px" marginBottom="24px">
            <Block fontSize="20px" fontWeight={600} color="#1F2937" marginBottom="20px">
              {i18n.t('xk.settings.companyContext.information')}
            </Block>

            <Col>
              <Block fontSize="16px" fontWeight={500} color="#374151" marginBottom="8px">
                {i18n.t('xk.settings.companyContext.title')}
              </Block>
              <Block fontSize="14px" color="#6B7280" marginBottom="16px" lineHeight="1.5" whiteSpace="pre-wrap">
                {i18n.t('xk.settings.companyContext.description')}
              </Block>

              <FormInput
                placeholder={i18n.t('xk.settings.companyContext.contentPlaceholder')}
                value={settings?.companyContext || ''}
                onSubmit={handleCompanyContextChange}
                debounce={2000}
                richTextEnabled={true}
                minHeight="120px"
                maxHeight="400px"
              />

              <Block fontSize="12px" color="#9CA3AF" marginTop="8px" lineHeight="1.4">
                {i18n.t('xk.settings.companyContext.tips')}
              </Block>
            </Col>
          </Block>
        </motion.div>

        <motion.div initial={{opacity: 0, y: 20}} animate={{opacity: 1, y: 0}} transition={{duration: 0.3, delay: 0.15}}>
          <Block backgroundColor="#FFFFFF" borderRadius="12px" boxShadow="0 1px 3px rgba(0, 0, 0, 0.1)" padding="24px" marginBottom="24px">
            <Block fontSize="20px" fontWeight={600} color="#1F2937" marginBottom="20px">
              {i18n.t('xk.settings.s3setup.title')}
            </Block>
            <Col>
              <Block fontSize="16px" fontWeight={500} color="#374151" marginBottom="8px">
                {i18n.t('xk.settings.s3setup.headline')}
              </Block>
              <Block fontSize="14px" color="#6B7280" marginBottom="16px" lineHeight="1.5" whiteSpace="pre-wrap">
                {i18n.t('xk.settings.s3setup.description')}
              </Block>
            </Col>
            <Col gap="16px">
              <Block>
                <Row>
                  <Block fontSize="14px" color="#6B7280" marginBottom="8px" lineHeight="1.5" whiteSpace="pre-wrap">
                    {i18n.t('xk.settings.s3setup.accessKeyPlaceholder')}
                  </Block>
                  <Block component="span" color="#DC2626">
                    *
                  </Block>
                </Row>
                <DynamicFormField
                  placeholder={i18n.t('xk.settings.s3setup.accessKeyPlaceholder')}
                  value={settings?.hasS3AccessKey ? '••••••••••••' : ''}
                  onBlur={(value: any) => handleS3AccessKeyChange(value)}
                  type="password"
                />
              </Block>
              <Block>
                <Row>
                  <Block fontSize="14px" color="#6B7280" marginBottom="8px" lineHeight="1.5" whiteSpace="pre-wrap">
                    {i18n.t('xk.settings.s3setup.secretKeyPlaceholder')}
                  </Block>
                  <Block component="span" color="#DC2626">
                    *
                  </Block>
                </Row>
                <DynamicFormField
                  placeholder={i18n.t('xk.settings.s3setup.secretKeyPlaceholder')}
                  value={settings?.hasS3SecretKey ? '••••••••••••' : ''}
                  onBlur={(value: any) => handleS3SecretKeyChange(value)}
                  type="password"
                />
              </Block>
              <Block>
                <Row>
                  <Block fontSize="14px" color="#6B7280" marginBottom="8px" lineHeight="1.5" whiteSpace="pre-wrap">
                    {i18n.t('xk.settings.s3setup.bucketNamePlaceholder')}
                  </Block>
                  <Block component="span" color="#DC2626">
                    *
                  </Block>
                </Row>
                <DynamicFormField
                  placeholder={i18n.t('xk.settings.s3setup.bucketNamePlaceholder')}
                  value={settings?.s3BucketName || ''}
                  onBlur={(value: any) => handleS3BucketNameChange(value)}
                />
              </Block>
              <Block>
                <Block fontSize="14px" color="#6B7280" marginBottom="8px" lineHeight="1.5" whiteSpace="pre-wrap">
                  {i18n.t('xk.settings.s3setup.regionPlaceholder')}
                </Block>
                <DynamicFormField
                  placeholder={i18n.t('xk.settings.s3setup.regionPlaceholder')}
                  value={settings?.s3Region || ''}
                  onBlur={(value: any) => handleS3RegionChange(value)}
                />
              </Block>
              <Block>
                <Block fontSize="14px" color="#6B7280" marginBottom="8px" lineHeight="1.5" whiteSpace="pre-wrap">
                  {i18n.t('xk.settings.s3setup.endpointPlaceholder')}
                </Block>
                <DynamicFormField
                  placeholder={i18n.t('xk.settings.s3setup.endpointPlaceholder')}
                  value={settings?.s3Endpoint || ''}
                  onBlur={(value: any) => handleS3EndpointChange(value)}
                />
              </Block>
            </Col>
            <Col>
              <Block fontSize="16px" fontWeight={500} color="#374151" marginTop="18px" marginBottom="8px">
                {i18n.t('xk.settings.s3setup.webhookHeadline')}
              </Block>
            </Col>
            <Col gap="16px">
              <DynamicFormField
                placeholder={i18n.t('xk.settings.s3setup.webhookEndpointPlaceholder')}
                value={settings?.webhookEndpoint || ''}
                onBlur={(value: any) => handleWebhookEndpointChange(value)}
              />
            </Col>
          </Block>
        </motion.div>

        <motion.div initial={{opacity: 0, y: 20}} animate={{opacity: 1, y: 0}} transition={{duration: 0.3, delay: 0.2}}>
          <Block backgroundColor="#F8F9FA" borderRadius="12px" border="1px solid #E5E7EB" padding="20px" marginBottom="20px">
            <Row alignItems="flex-start">
              <Block
                width="20px"
                height="20px"
                backgroundColor="#3B82F6"
                borderRadius="50%"
                marginRight="12px"
                marginTop="2px"
                fontSize="12px"
                color="#FFFFFF"
                display="flex"
                alignItems="center"
                justifyContent="center"
                fontWeight={700}
              >
                i
              </Block>
              <Col flex="1">
                <Block fontSize="14px" fontWeight={500} color="#374151" marginBottom="4px">
                  {i18n.t('xk.settings.autoSave')}
                </Block>
                <Block fontSize="13px" color="#6B7280" lineHeight="1.5">
                  {i18n.t('xk.settings.footer')}
                </Block>
              </Col>
            </Row>
          </Block>
        </motion.div>
      </Col>
    </Row>
  )
}

export default () => {
  return (
    <SettingsContextProvider>
      <Settings />
    </SettingsContextProvider>
  )
}
